import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CheckIcon,
  ChevronsUpDownIcon,
  CircleIcon,
  LockIcon,
  LockKeyholeOpenIcon,
  PlusIcon
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
  CREATE_ALLOCATION,
  GET_ALL_FUNDS,
  GET_ALLOCATION_DETAILS,
  SUBMIT_FOR_REVIEW
} from '../../queries';
import type {
  CreateAllocationResponse,
  EmployeesAndDealSelects,
  GetAccounts,
  GetAllFundsData,
  GetAllocationDetailsData
} from '../../types';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import NumberField from '@/components/NumberField';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText
} from '@/components/ui/input-group';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn, formatCurrency, formatPercent } from '@/lib/utils';

interface RequestAllocationDialogProps {
  /** Custom trigger element */
  children?: React.ReactNode;
  /** Accounts data for company selection */
  data3?: GetAccounts;
  /** Data for dropdowns */
  dataEmployeesAndDealSelects?: EmployeesAndDealSelects;
  /** The deal ID if it's an existing deal */
  dealId?: string;
  /** Deal name for display */
  dealName?: null | string;
  /** Whether this is for an existing deal or a new deal */
  isExistingDeal: boolean;
  /** SM proposed debt investment from the overview page */
  smProjectedDebtInvestment?: null | number;
  /** SM proposed equity investment from the overview page */
  smProjectedEquityInvestment?: null | number;
  targetEntityId: {
    __typename: 'AccountNode';
    id: string;
    opstreamAssets:
      | {
          id: string;
          previouslyParticipatingFundsIds:
            | {
                id: string;
              }[]
            | null;
        }[]
      | null;
  } | null;
}

export const RequestAllocationDialog = ({
  children,
  data3,
  dataEmployeesAndDealSelects,
  dealId,
  isExistingDeal,
  smProjectedDebtInvestment,
  smProjectedEquityInvestment,
  targetEntityId
}: RequestAllocationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [reviewTab, setReviewTab] = useState<'info' | 'lines' | 'overview'>(
    'info'
  );
  const [createdAllocationId, setCreatedAllocationId] = useState<null | string>(
    null
  );

  // Form state for new deal flow
  const [companyName, setCompanyName] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [projectName, setProjectName] = useState('');
  const [smcSource, setSmcSource] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [open1, setOpen1] = useState(false);
  const [open2, setOpen2] = useState(false);

  // Allocation request form state
  const [allocationAmount, setAllocationAmount] = useState('');
  const [debtAllocationAmount, setDebtAllocationAmount] = useState('');
  const [equityAllocationAmount, setEquityAllocationAmount] = useState('');
  const [useOfProceeds, setUseOfProceeds] = useState('');

  const [selectedFunds, setSelectedFunds] = useState<string[]>([]);
  const [excludedSmaImaFunds, setExcludedSmaImaFunds] = useState<string[]>([]);

  // Fund IDs from previously participating funds — these are locked (pre-selected, can't exclude)
  const lockedFundIds = useMemo(() => {
    const ids =
      targetEntityId?.opstreamAssets?.[0]?.previouslyParticipatingFundsIds?.map(
        f => f.id
      ) ?? [];
    return new Set(ids);
  }, [targetEntityId]);

  const allocationType =
    (targetEntityId?.opstreamAssets?.[0]?.previouslyParticipatingFundsIds
      ?.length ?? 0) > 0
      ? 'existing_platform'
      : 'new_platform';

  // Fetch all funds from backend
  const {
    data: fundsData,
    error: fundsError,
    loading: fundsLoading
  } = useQuery<GetAllFundsData>(GET_ALL_FUNDS, {
    variables: {
      fundTypeCodes: null,
      isActive: null,
      isSmaIma: null,
      status: null
    }
  });

  // Sync SM proposed values and pre-select funds when dialog opens
  useEffect(() => {
    if (open) {
      const debt = smProjectedDebtInvestment
        ? smProjectedDebtInvestment.toString()
        : '';
      const equity = smProjectedEquityInvestment
        ? smProjectedEquityInvestment.toString()
        : '';
      setDebtAllocationAmount(debt);
      setEquityAllocationAmount(equity);
      setAllocationAmount(
        debt || equity
          ? (
              (smProjectedDebtInvestment ?? 0) +
              (smProjectedEquityInvestment ?? 0)
            ).toString()
          : ''
      );

      const participatingFundIds =
        targetEntityId?.opstreamAssets?.[0]?.previouslyParticipatingFundsIds?.map(
          f => f.id
        ) ?? [];
      if (participatingFundIds.length > 0) {
        // Existing platform: pre-select participating funds
        setSelectedFunds(participatingFundIds);
      } else if (fundsData) {
        // New platform: pre-select all open + private funds that pass the balance/buyingPower filter
        const openPrivateIds = fundsData.funds.edges
          .filter(
            edge =>
              edge.node.balance != 0 &&
              edge.node.buyingPower != 0 &&
              edge.node.classification?.toLowerCase() === 'private' &&
              edge.node.status?.toLowerCase() === 'open'
          )
          .map(edge => edge.node.id);
        setSelectedFunds(openPrivateIds);
      }
    }
  }, [
    open,
    smProjectedDebtInvestment,
    smProjectedEquityInvestment,
    targetEntityId,
    fundsData
  ]);

  // Fetch allocation details after creation (for Step 3 review)
  const { data: allocationDetailsData, loading: allocationDetailsLoading } =
    useQuery<GetAllocationDetailsData>(GET_ALLOCATION_DETAILS, {
      skip: !createdAllocationId,
      variables: { id: createdAllocationId }
    });

  const availableFunds = useMemo(() => {
    const fundsToFilter = fundsData?.funds?.edges?.map(edge => edge.node) ?? [];
    if (allocationType === 'new_platform') {
      return fundsToFilter.filter(fund => {
        return (
          fund.buyingPower != 0 &&
          fund.balance != 0 &&
          fund.canAcceptNewPlatforms
        );
      });
    }
    return fundsToFilter.filter(
      fund => fund.balance != 0 && fund.buyingPower != 0
    );
  }, [allocationType, fundsData]);

  const decodedDealId = useMemo(() => {
    if (!dealId) return null;
    try {
      return atob(dealId).split(':')[1];
    } catch {
      return null;
    }
  }, [dealId]);

  // GraphQL mutations
  const [createAllocation, { loading: isCreatingAllocation }] =
    useMutation<CreateAllocationResponse>(CREATE_ALLOCATION, {
      refetchQueries: [
        {
          query: gql`
            query GetAllocationHistory($opportunityId: ID!) {
              allocationHistory(opportunityId: $opportunityId) {
                id
                allocationAmount
                debtAllocationAmount
                equityAllocationAmount
                version
                status
                allocationType
                createdDate
                totalAllocated
                numberOfFunds
                hasWarnings
                hasOverrides
                isFulfilled
              }
            }
          `,
          variables: { opportunityId: decodedDealId }
        }
      ]
    });

  const [submitForReview, { loading: isSubmittingForReview }] = useMutation<{
    submitForReview: {
      allocation: { id: string; status: string } | null;
      errors: null | string[];
      ok: boolean;
    };
  }>(SUBMIT_FOR_REVIEW, {
    refetchQueries: [
      {
        query: gql`
          query GetAllocationHistory($opportunityId: ID!) {
            allocationHistory(opportunityId: $opportunityId) {
              id
              allocationAmount
              debtAllocationAmount
              equityAllocationAmount
              version
              status
              allocationType
              createdDate
              totalAllocated
              numberOfFunds
              hasWarnings
              hasOverrides
              isFulfilled
            }
          }
        `,
        variables: { opportunityId: decodedDealId }
      }
    ]
  });

  const resetForm = () => {
    setStep(1);
    setReviewTab('info');
    setCreatedAllocationId(null);
    setCompanyName(null);
    setProjectName('');
    setSmcSource(null);
    setAllocationAmount('');
    setDebtAllocationAmount('');
    setEquityAllocationAmount('');
    setUseOfProceeds('');
    setSelectedFunds([]);
    setExcludedSmaImaFunds([]);
    setOpen1(false);
    setOpen2(false);
  };

  const toggleSmaImaExclusion = (fundId: string) => {
    setExcludedSmaImaFunds(prev => {
      if (prev.includes(fundId)) {
        // Currently excluded, remove from exclusion list (include it)
        return prev.filter(id => id !== fundId);
      } else {
        // Currently included, add to exclusion list (exclude it)
        return [...prev, fundId];
      }
    });
  };

  const toggleFundSelection = (fundId: string) => {
    setSelectedFunds(prev =>
      prev.includes(fundId)
        ? prev.filter(id => id !== fundId)
        : [...prev, fundId]
    );
  };

  const totalAllocated = useMemo(() => {
    if (selectedFunds.length === 0) return 0;

    return availableFunds
      .filter(f => selectedFunds.includes(f.id))
      .reduce((sum, fund) => {
        const available = fund.buyingPower;
        const requestedAmount = parseFloat(allocationAmount) || 0;
        const perFund = requestedAmount / selectedFunds.length;
        return sum + Math.min(perFund, available);
      }, 0);
  }, [selectedFunds, availableFunds, allocationAmount]);

  const handleSubmitForReview = async () => {
    if (!createdAllocationId) {
      alert('No allocation created yet');
      return;
    }

    try {
      // Decode the allocation ID
      const decodedAllocationId = atob(createdAllocationId).split(':')[1];

      const response = await submitForReview({
        variables: {
          allocationId: decodedAllocationId
        }
      });

      if (response.data?.submitForReview.ok) {
        setOpen(false);
        resetForm();
      } else if (response.data?.submitForReview.errors) {
        const errors = response.data.submitForReview.errors;
        alert('Error submitting for review:\n' + errors.join('\n'));
      }
    } catch (error) {
      alert(
        'Failed to submit for review: ' +
          (error instanceof Error ? error.message : 'Unknown error')
      );
    }
  };

  return (
    <Dialog
      onOpenChange={newOpen => {
        setOpen(newOpen);
        if (!newOpen) {
          resetForm();
        }
      }}
      open={open}
    >
      <DialogTrigger asChild>
        {children || (
          <Button>
            <PlusIcon />
            Request Allocation
          </Button>
        )}
      </DialogTrigger>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            Create Liquidity Allocation
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="grid max-h-full gap-4 overflow-y-auto">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  {step === 1 ? (
                    <BreadcrumbPage>Step 1</BreadcrumbPage>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Step 1
                    </span>
                  )}
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {step === 2 ? (
                    <BreadcrumbPage>Step 2</BreadcrumbPage>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Step 2
                    </span>
                  )}
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  {step === 3 ? (
                    <BreadcrumbPage>Step 3</BreadcrumbPage>
                  ) : (
                    <span className="text-muted-foreground text-sm">
                      Step 3
                    </span>
                  )}
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            {/* Step 1: Deal Info (only for new deals) */}
            {!isExistingDeal && step === 1 && (
              <FieldGroup className="grid grid-cols-2 gap-2.5">
                <Field>
                  <FieldLabel
                    className="inline-block"
                    htmlFor="project-name-field"
                  >
                    Project Name <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="project-name-field"
                    onChange={event => {
                      setProjectName(event.target.value);
                    }}
                    placeholder="Enter project name"
                    required
                    value={projectName}
                  />
                </Field>

                <Field>
                  <FieldLabel
                    className="inline-block"
                    htmlFor="smc-source-field"
                  >
                    SMC Source <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Popover
                    modal={true}
                    onOpenChange={setOpen1}
                    open={open1}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        aria-expanded={open1}
                        className="w-full justify-between"
                        id="smc-source-field"
                        role="combobox"
                        variant="outline"
                      >
                        {smcSource?.name ?? 'Select an option'}
                        <ChevronsUpDownIcon className="opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput
                          className="h-9"
                          placeholder="Search SMC source..."
                          required
                        />
                        <CommandList>
                          <CommandEmpty>No SMC source found.</CommandEmpty>
                          <CommandGroup>
                            {dataEmployeesAndDealSelects?.hrEmployees.edges.map(
                              edge => (
                                <CommandItem
                                  key={edge.node.id}
                                  onSelect={() => {
                                    setSmcSource(current =>
                                      current?.id === edge.node.id
                                        ? null
                                        : {
                                            id: edge.node.id,
                                            name: edge.node.name
                                          }
                                    );
                                    setOpen1(false);
                                  }}
                                  value={edge.node.name}
                                >
                                  {edge.node.name}
                                  <CheckIcon
                                    className={cn(
                                      'ml-auto',
                                      smcSource?.id === edge.node.id
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                    )}
                                  />
                                </CommandItem>
                              )
                            )}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field>
                  <FieldLabel htmlFor="company-name-field">
                    Company Name
                  </FieldLabel>
                  <Popover
                    modal={true}
                    onOpenChange={setOpen2}
                    open={open2}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        aria-expanded={open2}
                        className="justify-between"
                        id="company-name-field"
                        role="combobox"
                        variant="outline"
                      >
                        {companyName?.name ?? 'Select an option'}
                        <ChevronsUpDownIcon className="opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto min-w-[var(--radix-popover-trigger-width)] p-0">
                      <Command>
                        <CommandInput
                          className="h-9"
                          placeholder="Search company name..."
                        />
                        <CommandList>
                          <CommandEmpty>No company name found.</CommandEmpty>
                          <CommandGroup>
                            {data3?.searchAccounts.map(searchAccount => (
                              <CommandItem
                                key={searchAccount.id}
                                onSelect={() => {
                                  setCompanyName(current =>
                                    current?.id === searchAccount.id
                                      ? null
                                      : {
                                          id: searchAccount.id,
                                          name: searchAccount.name
                                        }
                                  );
                                  setOpen2(false);
                                }}
                                value={searchAccount.name}
                              >
                                {searchAccount.name}
                                <CheckIcon
                                  className={cn(
                                    'ml-auto',
                                    companyName?.id === searchAccount.id
                                      ? 'opacity-100'
                                      : 'opacity-0'
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </Field>
              </FieldGroup>
            )}

            {/* Step 1: Allocation Details (or Deal Info for new deals) */}
            {step === 1 && (
              <div className="space-y-5">
                <div>
                  <div className="text-lg font-semibold">
                    Allocation Details
                  </div>
                  <Separator className="mt-1 mb-2.5" />
                  <FieldGroup className="grid grid-cols-2 gap-2.5">
                    <Field>
                      <FieldLabel
                        className="inline-block"
                        htmlFor="debt-allocation-amount-field"
                      >
                        Debt Allocation Amount
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <NumberField
                          id="debt-allocation-amount-field"
                          onChange={event => {
                            const raw = event.target.value.replace(/,/g, '');
                            setDebtAllocationAmount(raw);
                            const debt = parseFloat(raw) || 0;
                            const equity =
                              parseFloat(equityAllocationAmount) || 0;
                            setAllocationAmount((debt + equity).toString());
                          }}
                          placeholder="Enter amount"
                          value={debtAllocationAmount}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>USD</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>

                    <Field>
                      <FieldLabel
                        className="inline-block"
                        htmlFor="equity-allocation-amount-field"
                      >
                        Equity Allocation Amount
                      </FieldLabel>
                      <InputGroup>
                        <InputGroupAddon>
                          <InputGroupText>$</InputGroupText>
                        </InputGroupAddon>
                        <NumberField
                          id="equity-allocation-amount-field"
                          onChange={event => {
                            const raw = event.target.value.replace(/,/g, '');
                            setEquityAllocationAmount(raw);
                            const debt = parseFloat(debtAllocationAmount) || 0;
                            const equity = parseFloat(raw) || 0;
                            setAllocationAmount((debt + equity).toString());
                          }}
                          placeholder="Enter amount"
                          value={equityAllocationAmount}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>USD</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>

                    <FieldSet className="w-full max-w-xs">
                      <FieldLegend variant="label">
                        Allocation Type <span className="text-red-500">*</span>
                      </FieldLegend>
                      <RadioGroup
                        defaultValue={allocationType}
                        disabled
                        id="allocation-type-field"
                      >
                        <Field orientation="horizontal">
                          <RadioGroupItem
                            id="new-platform"
                            value="new_platform"
                          />
                          <FieldLabel
                            className="font-normal"
                            htmlFor="new-platform"
                          >
                            New Platform
                          </FieldLabel>
                        </Field>
                        <Field orientation="horizontal">
                          <RadioGroupItem
                            id="existing-platform"
                            value="existing_platform"
                          />
                          <FieldLabel
                            className="font-normal"
                            htmlFor="existing-platform"
                          >
                            Existing Platform
                          </FieldLabel>
                        </Field>
                      </RadioGroup>
                    </FieldSet>

                    {allocationType === 'existing_platform' && (
                      <Field>
                        <FieldLabel
                          className="inline-block"
                          htmlFor="use-of-proceeds-field"
                        >
                          Use of Proceeds{' '}
                          <span className="text-red-500">*</span>
                        </FieldLabel>
                        <Select
                          onValueChange={setUseOfProceeds}
                          required
                          value={useOfProceeds}
                        >
                          <SelectTrigger id="use-of-proceeds-field">
                            <SelectValue placeholder="Select use of proceeds" />
                          </SelectTrigger>
                          <SelectContent>
                            {dataEmployeesAndDealSelects?.useOfProceedsSelections &&
                            dataEmployeesAndDealSelects.useOfProceedsSelections
                              .length > 0 ? (
                              dataEmployeesAndDealSelects.useOfProceedsSelections.map(
                                option => (
                                  <SelectItem
                                    key={option.id}
                                    value={option.value}
                                  >
                                    {option.name}
                                  </SelectItem>
                                )
                              )
                            ) : (
                              <>
                                <SelectItem value="acquisition_financing">
                                  Acquisition Financing
                                </SelectItem>
                                <SelectItem value="liquidity_financing">
                                  Liquidity Financing
                                </SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </Field>
                    )}
                  </FieldGroup>
                </div>
              </div>
            )}

            {/* Step 2: Fund Selection */}
            {step === 2 && (
              <div className="space-y-5">
                <div>
                  <div className="text-lg font-semibold">Fund Selection</div>
                  <Separator className="mt-1 mb-2.5" />
                  {fundsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Spinner className="size-6 text-blue-500" />
                    </div>
                  ) : fundsError ? (
                    <div className="text-destructive py-4 text-center text-sm">
                      Error loading funds: {fundsError.message}
                    </div>
                  ) : availableFunds.length === 0 ? (
                    <div className="text-muted-foreground py-8 text-center text-sm">
                      No funds available for allocation
                    </div>
                  ) : (
                    <div className="rounded-md border bg-white p-5">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Name</TableHead>
                            {allocationType === 'new_platform' ? (
                              <>
                                <TableHead>Classification</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-center">
                                  SMA/IMA
                                </TableHead>
                                <TableHead className="text-right">
                                  Buying Power
                                </TableHead>
                                <TableHead className="text-center">
                                  Include
                                </TableHead>
                              </>
                            ) : (
                              <>
                                <TableHead className="text-right">
                                  Balance
                                </TableHead>
                                <TableHead className="text-center">
                                  Include
                                </TableHead>
                                <TableHead className="text-center">
                                  SMA/IMA
                                </TableHead>
                              </>
                            )}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {availableFunds.map(fund => (
                            <TableRow key={fund.id}>
                              <TableCell className="font-medium">
                                {fund.name}
                              </TableCell>
                              {allocationType === 'new_platform' ? (
                                <>
                                  <TableCell>
                                    {fund.classification && (
                                      <Badge variant="pastel-slate">
                                        {fund.classification.toLowerCase() ===
                                        'private' ? (
                                          <LockIcon className="mr-1 h-3 w-3" />
                                        ) : (
                                          <LockKeyholeOpenIcon className="mr-1 h-3 w-3" />
                                        )}
                                        {fund.classification}
                                      </Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {fund.status && (
                                      <Badge
                                        variant={
                                          fund.status === 'open'
                                            ? 'pastel-green'
                                            : 'pastel-slate'
                                        }
                                      >
                                        <CircleIcon className="h-3 w-3" />
                                        {fund.status.charAt(0).toUpperCase() +
                                          fund.status.slice(1)}
                                      </Badge>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Checkbox
                                      checked={fund.isSmaIma}
                                      disabled
                                    />
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {formatCurrency(fund.buyingPower)}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Checkbox
                                      checked={selectedFunds.includes(fund.id)}
                                      onCheckedChange={() => {
                                        toggleFundSelection(fund.id);
                                      }}
                                    />
                                  </TableCell>
                                </>
                              ) : (
                                <>
                                  <TableCell className="text-right">
                                    {formatCurrency(fund.balance)}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {fund.isSmaIma ? (
                                      <Checkbox
                                        checked={selectedFunds.includes(
                                          fund.id
                                        )}
                                        onCheckedChange={() => {
                                          toggleFundSelection(fund.id);
                                        }}
                                      />
                                    ) : (
                                      <Checkbox
                                        checked={selectedFunds.includes(
                                          fund.id
                                        )}
                                        disabled
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    <Checkbox
                                      checked={fund.isSmaIma}
                                      disabled
                                    />
                                  </TableCell>
                                </>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  <div className="text-muted-foreground mt-2 text-sm">
                    Selected {selectedFunds.length} of {availableFunds.length}{' '}
                    funds
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <div className="space-y-2.5">
                <Tabs
                  onValueChange={value => {
                    setReviewTab(value as 'info' | 'lines' | 'overview');
                  }}
                  value={reviewTab}
                >
                  <TabsList className="w-full">
                    <TabsTrigger value="info">Allocation Info</TabsTrigger>
                    <TabsTrigger value="lines">Allocation Lines</TabsTrigger>
                    {allocationType === 'existing_platform' && (
                      <TabsTrigger value="overview">Overview</TabsTrigger>
                    )}
                  </TabsList>

                  <TabsContent
                    className="space-y-2.5"
                    value="info"
                  >
                    <div className="space-y-2.5">
                      <div className="text-lg font-semibold">
                        Allocation Info
                      </div>
                      <div className="rounded-md border bg-white p-5">
                        <div className="space-y-5">
                          {allocationDetailsData?.node &&
                            !allocationDetailsData.node.isFulfilled && (
                              <Alert className="border-amber-300 bg-amber-50">
                                <AlertDescription className="flex items-center gap-3 font-medium text-amber-500">
                                  <svg
                                    className="size-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                    <line
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      x1="12"
                                      x2="12"
                                      y1="9"
                                      y2="13"
                                    />
                                    <line
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      x1="12"
                                      x2="12.01"
                                      y1="17"
                                      y2="17"
                                    />
                                  </svg>
                                  Not Fulfilled
                                </AlertDescription>
                              </Alert>
                            )}
                          <div className="flex gap-5">
                            <div className="flex min-w-0 flex-1 gap-2.5 overflow-hidden pr-7.5 text-ellipsis whitespace-nowrap">
                              <div className="w-[300px] overflow-hidden text-sm text-ellipsis">
                                Number of Funds
                              </div>
                              <div className="text-muted-foreground overflow-hidden text-sm font-medium text-ellipsis">
                                {allocationDetailsData?.node?.numberOfFunds ??
                                  selectedFunds.length}
                              </div>
                            </div>
                            <div className="flex min-w-0 flex-1 gap-2.5 pr-7.5">
                              <div className="w-[300px] overflow-hidden text-sm text-ellipsis">
                                Allocation Type
                              </div>
                              <div>
                                <Badge variant="pastel-slate">
                                  {allocationDetailsData?.node
                                    ?.allocationType === 'new_platform'
                                    ? 'New Platform'
                                    : 'Existing Platform'}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-5">
                            <div className="flex min-w-0 flex-1 gap-2.5 overflow-hidden pr-7.5 text-ellipsis whitespace-nowrap">
                              <div className="w-[300px] overflow-hidden text-sm text-ellipsis">
                                Requested Amount
                              </div>
                              <div className="text-muted-foreground overflow-hidden text-sm font-medium text-ellipsis">
                                {allocationDetailsData?.node?.allocationAmount
                                  ? formatCurrency(
                                      allocationDetailsData.node
                                        .allocationAmount
                                    )
                                  : formatCurrency(
                                      parseFloat(allocationAmount) || 0
                                    )}
                              </div>
                            </div>
                            <div className="flex min-w-0 flex-1 gap-2.5 overflow-hidden pr-7.5 text-ellipsis whitespace-nowrap">
                              <div className="w-[300px] overflow-hidden text-sm text-ellipsis">
                                Use of Proceeds
                              </div>
                              <div className="text-muted-foreground overflow-hidden text-sm font-medium text-ellipsis">
                                {allocationDetailsData?.node?.useOfProceeds ||
                                  useOfProceeds ||
                                  '-'}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-5">
                            <div className="flex min-w-0 flex-1 gap-2.5 overflow-hidden pr-7.5 text-ellipsis whitespace-nowrap">
                              <div className="w-[300px] overflow-hidden text-sm text-ellipsis">
                                Allocated Amount
                              </div>
                              <div className="text-muted-foreground overflow-hidden text-sm font-medium text-ellipsis">
                                {allocationDetailsData?.node?.totalAllocated
                                  ? formatCurrency(
                                      allocationDetailsData.node.totalAllocated
                                    )
                                  : formatCurrency(totalAllocated)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent
                    className="space-y-2.5"
                    value="lines"
                  >
                    <div className="space-y-2.5">
                      <div className="text-lg font-semibold">
                        Allocation Lines
                      </div>
                      <div className="rounded-md border bg-white p-5">
                        {allocationDetailsLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <Spinner className="size-6 text-blue-500" />
                          </div>
                        ) : allocationDetailsData?.node?.allocationLines ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Fund</TableHead>
                                <TableHead className="text-right">
                                  Allocated Amount
                                </TableHead>
                                <TableHead className="text-right">
                                  Allocation Percentage
                                </TableHead>
                                <TableHead className="text-right">
                                  Allocation Concentration Percentage
                                </TableHead>
                                <TableHead className="text-right">
                                  Concentration Limit Amount
                                </TableHead>
                                <TableHead className="text-right">
                                  Over Limit
                                </TableHead>
                                <TableHead className="text-right">
                                  Balance
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {allocationDetailsData.node.allocationLines.map(
                                line => (
                                  <TableRow key={line.id}>
                                    <TableCell className="font-medium">
                                      {line.fund.name}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(line.allocatedAmount)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatPercent(line.allocationPercentage)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatPercent(
                                        line.concentrationLimitPct
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(
                                        line.concentrationLimitAmount || 0
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {line.isOverLimit ? (
                                        <span className="text-destructive">
                                          Yes
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground">
                                          -
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      {formatCurrency(line.fund.balance)}
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="text-muted-foreground py-8 text-center text-sm">
                            No allocation lines available
                          </div>
                        )}
                      </div>
                    </div>
                  </TabsContent>

                  {allocationType === 'existing_platform' && (
                    <TabsContent
                      className="space-y-2.5"
                      value="overview"
                    >
                      <div className="space-y-2.5">
                        <div className="text-lg font-semibold">Overview</div>
                        <div className="rounded-md border bg-white p-5">
                          <div className="text-muted-foreground text-center text-sm">
                            Overview content will be displayed here
                          </div>
                        </div>
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter className="flex justify-end">
          {step === 1 && (
            <Button
              disabled={
                (!debtAllocationAmount && !equityAllocationAmount) ||
                !allocationType ||
                (allocationType === 'existing_platform' && !useOfProceeds)
              }
              onClick={() => {
                setStep(2);
              }}
            >
              Next
            </Button>
          )}
          {step === 2 && (
            <>
              <Button
                onClick={() => {
                  setStep(1);
                }}
                variant="outline"
              >
                Back
              </Button>
              <Button
                disabled={selectedFunds.length === 0 || isCreatingAllocation}
                onClick={async () => {
                  const parseNumber = (value: string) => {
                    const trimmedValue = value.trim();
                    if (!trimmedValue) return 0;
                    const parsedValue = Number(trimmedValue);
                    return Number.isNaN(parsedValue) ? 0 : parsedValue;
                  };

                  if (!decodedDealId || !allocationType) return;

                  const parsedDebtAmount = parseNumber(debtAllocationAmount);
                  const parsedEquityAmount = parseNumber(
                    equityAllocationAmount
                  );

                  if (parsedDebtAmount === 0 && parsedEquityAmount === 0)
                    return;

                  if (
                    allocationType === 'existing_platform' &&
                    !useOfProceeds
                  ) {
                    alert(
                      'Use of proceeds is required for existing platform allocations'
                    );
                    return;
                  }

                  try {
                    const decodedFundIds = selectedFunds.map(fundId => {
                      try {
                        return atob(fundId).split(':')[1];
                      } catch {
                        return fundId;
                      }
                    });

                    const decodedExcludedFunds = excludedSmaImaFunds.map(
                      fundId => {
                        try {
                          return atob(fundId).split(':')[1];
                        } catch {
                          return fundId;
                        }
                      }
                    );

                    const mutationVariables: Record<string, any> = {
                      allocationType: allocationType,
                      debtAllocationAmount: parsedDebtAmount,
                      equityAllocationAmount: parsedEquityAmount,
                      opportunityId: decodedDealId,
                      participantFunds: decodedFundIds
                    };

                    if (allocationType === 'existing_platform') {
                      mutationVariables.useOfProceeds = useOfProceeds;
                      if (decodedExcludedFunds.length > 0) {
                        mutationVariables.excludedSmaImaFunds =
                          decodedExcludedFunds;
                      }
                    } else {
                      mutationVariables.includedFundTypes = undefined;
                      mutationVariables.excludedFundTypes = [];
                      mutationVariables.includeOpenPrivateFunds = true;
                    }

                    const response = await createAllocation({
                      variables: {
                        values: mutationVariables
                      }
                    });

                    if (response.data?.createAllocation.ok) {
                      const allocationId =
                        response.data.createAllocation.allocation?.id;
                      setCreatedAllocationId(allocationId ?? null);
                      setStep(3);
                    } else if (response.data?.createAllocation.errors) {
                      const errors = response.data.createAllocation.errors;
                      alert('Error creating allocation:\n' + errors.join('\n'));
                    }
                  } catch (error) {
                    alert(
                      'Failed to generate allocation: ' +
                        (error instanceof Error
                          ? error.message
                          : 'Unknown error')
                    );
                  }
                }}
              >
                {isCreatingAllocation ? (
                  <>
                    <Spinner />
                    Requesting...
                  </>
                ) : (
                  'Request Draft Allocation'
                )}
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button
                onClick={() => {
                  setStep(2);
                }}
                variant="outline"
              >
                Back
              </Button>
              <Button
                disabled={
                  !createdAllocationId ||
                  allocationDetailsLoading ||
                  isSubmittingForReview
                }
                onClick={() => {
                  void handleSubmitForReview();
                }}
                type="submit"
              >
                {isSubmittingForReview ? (
                  <>
                    <Spinner />
                    Submitting...
                  </>
                ) : (
                  'Submit for Review'
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
